/*
  Sign in with the ClassTutor portal account. Ported from the portal's signin page (Formik + yup,
  AuthCard, the same wording), with "Forgot password" sent to the portal's existing flow.
*/
import { message } from "antd";
import logo from "app/assets/portal/logo-classtutor.png";
import { Button } from "app/components/v1/Button";
import { useLoginMutation } from "app/services/account";
import { errorMessage, storeTokens } from "app/services/index";
import { userSlice } from "app/store";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import * as yup from "yup";

const PORTAL = process.env.REACT_APP_PORTAL_URL || "https://portal.classtutor.co.uk";

const Page = styled.div`
  min-height: 100vh; background: #f8f9fc; display: flex; flex-direction: column; align-items: center; padding: calc(40px + var(--ct-safe-top)) 16px 32px;
  .auth-card { width: 100%; max-width: 440px; background: #fff; border-radius: 15px; border: 1px solid #e2e2ea; padding: 32px 24px; }
  h4 { text-align: center; font-size: 2rem; margin-bottom: 4px; }
  .sub { text-align: center; color: #92929d; margin-bottom: 24px; }
  label { font-weight: 600; color: #44444f; font-size: 14px; margin-bottom: 6px; display: block; }
  input.st { width: 100%; height: 50px; border: 1px solid #d2d2d5; border-radius: 10px; padding: 0 14px; font-size: 16px; background: #fff; color: #171725; outline: none; }
  input.st:focus { border-color: #4c54a0; box-shadow: 0 0 0 4px #4c54a022; }
  .err { color: #fc5a5a; font-size: 13px; margin-top: 6px; }
`;

export default function Signin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTarget = (location.state as { redirectTarget?: string } | null)?.redirectTarget;
  const [login, { isLoading }] = useLoginMutation();

  const formik = useFormik({
    initialValues: { email: "", password: "", rememberMe: true },
    validationSchema: yup.object({
      email: yup.string().required("Please enter your email or username"),
      password: yup.string().trim().required("Please enter your password").max(100),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      try {
        const res = await login({ identifier: values.email.trim(), password: values.password, rememberMe: values.rememberMe }).unwrap();
        storeTokens(res.jwt, res.refreshToken);
        dispatch(userSlice.actions.signedIn(res.profile));
        navigate(redirectTarget && redirectTarget !== "/login" ? redirectTarget : "/", { replace: true });
      } catch (e) {
        message.error(errorMessage(e, "We could not sign you in. Please try again."));
      }
    },
  });

  return (
    <Page>
      <img src={logo} alt="ClassTutor" height={36} className="mb-5" />
      <div className="auth-card">
        <h4>Login</h4>
        <p className="sub">Use the same email and password as the ClassTutor portal.</p>
        <form onSubmit={(e) => { e.preventDefault(); void formik.submitForm(); }} noValidate>
          <div className="mb-3">
            <label htmlFor="email">Your Email / Username</label>
            <input id="email" className="st" type="email" inputMode="email" autoComplete="username" autoCapitalize="none" value={formik.values.email} onChange={(e) => formik.setFieldValue("email", e.target.value)} />
            {formik.errors.email && <div className="err">{formik.errors.email}</div>}
          </div>
          <div className="mb-3">
            <label htmlFor="password">Enter Password</label>
            <input id="password" className="st" type="password" autoComplete="current-password" value={formik.values.password} onChange={(e) => formik.setFieldValue("password", e.target.value)} />
            {formik.errors.password && <div className="err">{formik.errors.password}</div>}
          </div>
          <div className="d-flex align-items-center justify-content-between my-3">
            <label className="d-flex align-items-center gap-2 m-0" style={{ fontWeight: 500 }}>
              <input type="checkbox" checked={formik.values.rememberMe} onChange={(e) => formik.setFieldValue("rememberMe", e.target.checked)} /> Remember me
            </label>
            <a href={`${PORTAL}/forgot-password`} target="_blank" rel="noreferrer">Forgot Password</a>
          </div>
          <Button type="submit" className="w-100" size="lg" name="Login" loading={isLoading} />
        </form>
      </div>
      <p className="text-center mt-4 ct-muted" style={{ maxWidth: 360, fontSize: 13 }}>
        No account yet? Your ClassTutor welcome email has your sign-in link, or message us on <a href="https://classtutor.co.uk" target="_blank" rel="noreferrer">classtutor.co.uk</a>.
      </p>
    </Page>
  );
}
